import { makeAutoObservable } from 'mobx';
import _ from 'lodash';
import {auth} from 'main/tools';
import employeeTimeOffStore from 'main/stores/time_off/EmployeeTimeOffStore';
import moment from 'moment';

class EmployeeTimeOffState {
  store = employeeTimeOffStore;
  parentState;
  history;
  employeeId;

  upcomingHolidaysModalOpen = false;
  accounts = [];
  upcomingHolidays = [];
  activeRequest = null;
  notesModalOpen = false;
  policy;
  policySummaryModalOpen = false;
  unlimitedPolicySummaryModalOpen = false;
  policySummaryAccount;
  requestsInteractiveAgent;
  transactionsInteractiveAgent;
  editingManualAdjustment = {};
  errors = {};
  transactionsForecastDate = moment();
  selectedLiabilityBalance = {};

  constructor() {
    makeAutoObservable(this, {
      store: false,
      history: false,
      requestsInteractiveAgent: false,
      transactionsInteractiveAgent: false
    });
  }

  receiveProps({parentState, history}) {
    this.parentState = parentState;
    this.employeeId = parentState.match.params.id;
    this.history = history;
  }

  async load() {
    await this.store.load(this.employeeId);

    this.accounts = this.store.getAccounts();
    this.policy = this.store.getPolicy();
    this.upcomingHolidays = this.store.getUpcomingHolidays();
    this.employee = this.store.getEmployeeDetails();
  }

  requestTimeOff() {
    this.history.push(`time-off/request`);
  }

  openUpcomingHolidaysModal() {
    this.upcomingHolidaysModalOpen = true;
  }

  closeUpcomingHolidaysModal() {
    this.upcomingHolidaysModalOpen = false;
  }

  redirectToEdit(request) {
    this.history.push(`/${this.employeeId}/time-off/request/${request.id}/edit`);
  }

  async deleteRequest(request) {
    await this.store.destroy(request);
  }

  showNotesModal(request) {
    this.activeRequest = request;
    this.notesModalOpen = true;
  }

  closeNotesModal() {
    this.notesModalOpen = false;
  }

  setRequestsInteractiveAgent(agent) {
    this.requestsInteractiveAgent = agent;
  }

  setTransactionsInteractiveAgent(agent) {
    this.transactionsInteractiveAgent = agent;
  }

  openPolicySummaryModal(policySummaryAccount) {
    this.policySummaryAccount = policySummaryAccount;

    if (policySummaryAccount.unlimited) {
      this.unlimitedPolicySummaryModalOpen = true;
    } else {
      this.policySummaryModalOpen = true;
    }
  }

  closePolicySummaryModals() {
    this.policySummaryModalOpen = false;
    this.unlimitedPolicySummaryModalOpen = false;
  }

  updateTransactionsForecastDate(date) {
    this.transactionsForecastDate = date || moment();
  }

  async deleteManualAdjustment(transaction) {
    await this.store.destroy(transaction);
    this.transactionsInteractiveAgent.refresh();
  }

  async deleteTimeOffRequest(request) {
    await this.store.destroy(request);
    this.requestsInteractiveAgent.refresh();
    this.transactionsInteractiveAgent.refresh();
  }

  async saveManualAdjustment() {
    const {model, errors} = await this.store.patch(this.editingManualAdjustment);

    this.errors = errors;

    if (model) {
      this.closeEditManualAdjustmentModal();
      this.transactionsInteractiveAgent.refresh();
    }
  }

  get showRequestTimeOffButton() {
    return auth.employee.id === this.employeeId &&
      !this.parentState.employee.terminated;
  }

  get showRecordTimeOffButton() {
    if (this.showRequestTimeOffButton) return false;
    return auth.hasAccess('::MANAGE_TIME_OFF') ||
      this.parentState.editDataPermission('::TIME_OFF');
  }

  get currentEmployee() {
    return this.parentState.employee;
  }

  get accountFilterOptions() {
    return this.accounts.map(account => ({
      id: account.id,
      display: account.timeOffType.name
    }));
  }

  get timeOffEditable() {
    return !this.parentState.employee.terminated && auth.hasAccess('::MANAGE_TIME_OFF');
  }

  accountUnit(accountId) {
    return _.find(this.accounts, {id: String(accountId)}).timeOffType.unit;
  }

  transactionListLatestForYear(transactions) {
    return _.find(transactions, transaction => transaction.balance != null) || _.find(transactions, transaction => transaction.logged != null);
  }
}

export default EmployeeTimeOffState;
